/**
 * The two live allocation statuses, shared by every frontend surface.
 *
 * `CONFIRMED` is a firm commitment; `PROPOSED` is pencilled in but not yet
 * agreed. Both count in full toward capacity, utilisation, conflicts, planned
 * cost, ROI and margin — the distinction is presentational, alerting and
 * filtering only, and nothing in the money path branches on it. The backend
 * keeps the matching definition in
 * `services/backend-api/src/allocations/allocation-status.util.ts`.
 *
 * `PLANNED`, `IN_PROGRESS` and `COMPLETED` are retired: every row was
 * backfilled to `CONFIRMED` and the values dropped from the enum, so no read
 * needs to exclude them. The coercion in {@link toAllocationStatus} still has
 * to cope with one, because a cached payload or an open tab can outlive the
 * migration.
 */
export type AllocationStatus = 'PROPOSED' | 'CONFIRMED';

/** Display order for pickers and legends — the default leads. */
export const ALLOCATION_STATUSES: readonly AllocationStatus[] = ['CONFIRMED', 'PROPOSED'] as const;

/** What a new allocation is unless the planner says otherwise (matches the column default). */
export const DEFAULT_ALLOCATION_STATUS: AllocationStatus = 'CONFIRMED';

/** Sentence-case labels — the only place the user-facing wording is spelled. */
export const ALLOCATION_STATUS_LABEL: Record<AllocationStatus, string> = {
  CONFIRMED: 'Confirmed',
  PROPOSED: 'Proposed',
};

/**
 * Whether a status counts as live. Takes `unknown` because it also has to judge
 * a value that came off the wire or out of storage, where a retired or invented
 * string must be rejected rather than coerced.
 */
export const isLiveAllocationStatus = (status: unknown): status is AllocationStatus =>
  typeof status === 'string' && (ALLOCATION_STATUSES as readonly string[]).includes(status);

/** Whether an allocation is pencilled in rather than committed. */
export const isProposedAllocation = (status: unknown): boolean => status === 'PROPOSED';

/**
 * Narrows an arbitrary stored status onto the live pair, for seeding a form
 * control that can only offer the two. An unrecognised value falls back to the
 * default rather than leaving the control unset, which would post a blank.
 */
export const toAllocationStatus = (status: unknown): AllocationStatus =>
  isLiveAllocationStatus(status) ? status : DEFAULT_ALLOCATION_STATUS;

/**
 * Which statuses a list scopes itself to.
 *
 * Deliberately *not* a user-facing control on the planning board: the board's
 * proposed work gets its own tab rather than a toolbar filter, because a
 * persisted filter is something a planner can leave switched on and later read
 * a truncated list from, while a tab is somewhere they chose to be. This type
 * is the plumbing behind that tab (and any other status-scoped list), not a
 * preference.
 *
 * Whatever it is set to, it narrows what is **listed**, never what is
 * **counted** — utilisation, conflicts and cost count both statuses in full.
 */
export type AllocationStatusFilter = 'all' | 'confirmed' | 'proposed';

/**
 * Whether an allocation belongs in a list scoped this way. Normalises through
 * {@link toAllocationStatus} so a legacy or unrecognised status is treated as
 * `CONFIRMED` — the same fallback the form controls use, so a row can never
 * fall out of every scope at once.
 */
export const matchesAllocationStatusFilter = (
  status: unknown,
  filter: AllocationStatusFilter,
): boolean => {
  if (filter === 'all') return true;
  const normalised = toAllocationStatus(status);
  return filter === 'proposed' ? normalised === 'PROPOSED' : normalised === 'CONFIRMED';
};
